import { styled } from '@mui/material';

import { useMemo } from 'react';

import { I18nResources, useI18n } from '@app-velocity/i18n';
import { ArrowForwardIcon } from '@app-velocity/icons';
import { Card, Page } from '@app-velocity/ui';

import { ReportStatistics } from '../../types';

export type CompareDeltaOther = {
  label: string;
  stats: ReportStatistics;
};

export type CompareDeltaProps = {
  baseline?: ReportStatistics;
  baselineLabel?: string;
  others?: CompareDeltaOther[];
};

const getFill = (failure: boolean, success: boolean) => {
  if (failure) return 'var(--error-error-color)';
  if (success) return 'var(--success-success-icon-primary-color)';

  return 'var(--text-text-primary-color)';
};

const getRotation = (failure: boolean, success: boolean) => {
  if (failure) return 'rotate(45deg)';
  if (success) return 'rotate(-45deg)';

  return '';
};

const ResultIcon = styled('div')<{ success: boolean; failure: boolean }>(
  ({ theme, success, failure }) => ({
    transform: getRotation(failure, success),
    svg: {
      display: 'flex',
      height: theme.spacing(5),
      width: '100%',
      fill: getFill(failure, success),
    },
  })
);

const SingleMetricContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: `${theme.spacing(2)} 0`,
  borderBottom: '1px solid var(--stroke-stroke-primary-color)',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const Details = styled('div')({
  fontWeight: 400,
  fontSize: 16,
});

const Columns = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  alignItems: 'center',
  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
}));

const Column = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

const ColumnLabel = styled('span')({
  fontSize: 12,
  color: 'var(--text-text-secondary-color)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const ColumnValue = styled('div')(({ theme }) => ({
  fontWeight: 700,
  fontSize: 20,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const round = (val: number) => {
  if (isNaN(val)) return 0;

  return Math.round(val * 10000) / 10000;
};

const getTotalSimulations = (stats: ReportStatistics) =>
  stats.simulatedReviews.failed + stats.simulatedReviews.succeded;

type MetricDefinition = {
  unit: string;
  label: I18nResources;
  getValue: (stats: ReportStatistics) => number;
  format: (value: number) => string;
  higherIsBetter: boolean;
};

const METRIC_DEFINITIONS: MetricDefinition[] = [
  {
    unit: '%',
    label: 'prototypes:report.metric.success_rate',
    getValue: (stats) => (stats.metrics.successRatio || 0) * 100,
    format: (value) => value.toFixed(0),
    higherIsBetter: true,
  },
  {
    unit: 's',
    label: 'prototypes:report.metric.average_duration',
    getValue: (stats) => stats.metrics.avgDuration || 0,
    format: (value) => value.toFixed(0),
    higherIsBetter: false,
  },
  {
    unit: '%',
    label: 'prototypes:report.metric.drop_off_rate',
    getValue: (stats) => (stats.metrics.dropOffs || 0) * 100,
    format: (value) => value.toFixed(0),
    higherIsBetter: false,
  },
  {
    unit: '%',
    label: 'prototypes:report.metric.missclick_rate',
    getValue: (stats) => (stats.metrics.missClicks || 0) * 100,
    format: (value) => value.toFixed(0),
    higherIsBetter: false,
  },
  {
    unit: '',
    label: 'prototypes:report.metric.avg_sim_interactions',
    getValue: (stats) => {
      const total = getTotalSimulations(stats);

      return total ? stats.metrics.simulatedInteractions / total : 0;
    },
    format: (value) => value.toFixed(1),
    higherIsBetter: false,
  },
  {
    unit: '',
    label: 'prototypes:report.metric.avg_views_count',
    getValue: (stats) => {
      const total = getTotalSimulations(stats);

      return total ? stats.metrics.simulationViewsCount / total : 0;
    },
    format: (value) => value.toFixed(1),
    higherIsBetter: false,
  },
];

export const CompareDelta = ({
  baseline,
  baselineLabel,
  others,
}: CompareDeltaProps) => {
  const { t } = useI18n();

  const comparedMetrics = useMemo(() => {
    if (!baseline || !others || !others.length) return [];

    const baselineTotal = getTotalSimulations(baseline);
    if (!baselineTotal) return [];

    const validOthers = others.filter(
      (other) => getTotalSimulations(other.stats) > 0
    );
    if (!validOthers.length) return [];

    return METRIC_DEFINITIONS.map((definition) => {
      const baselineValue = definition.getValue(baseline);

      return {
        unit: definition.unit,
        label: definition.label,
        baselineValue: definition.format(baselineValue),
        columns: validOthers.map((other) => {
          const otherValue = definition.getValue(other.stats);
          const improved = definition.higherIsBetter
            ? round(otherValue) > round(baselineValue)
            : round(otherValue) < round(baselineValue);
          const regressed = definition.higherIsBetter
            ? round(otherValue) < round(baselineValue)
            : round(otherValue) > round(baselineValue);

          return {
            label: other.label,
            value: definition.format(otherValue),
            success: improved,
            failure: regressed,
          };
        }),
      };
    });
  }, [baseline, others]);

  if (!comparedMetrics.length || !baseline) return null;

  return (
    <Content>
      <Page.Subheader title={t('prototypes:compare.changes_vs_baseline')} />

      <Card>
        {comparedMetrics.map(
          ({ unit, label, baselineValue, columns }, index) => (
            <SingleMetricContainer key={index}>
              <Details>{t(label)}</Details>
              <Columns>
                <Column>
                  <ColumnLabel>
                    {baselineLabel || t('prototypes:compare.baseline')}
                  </ColumnLabel>
                  <ColumnValue>
                    {baselineValue}
                    <small>{unit}</small>
                  </ColumnValue>
                </Column>
                {columns.map((column) => (
                  <Column key={column.label}>
                    <ColumnLabel>{column.label}</ColumnLabel>
                    <ColumnValue>
                      <ResultIcon
                        success={column.success}
                        failure={column.failure}
                      >
                        <ArrowForwardIcon />
                      </ResultIcon>
                      {column.value}
                      <small>{unit}</small>
                    </ColumnValue>
                  </Column>
                ))}
              </Columns>
            </SingleMetricContainer>
          )
        )}
      </Card>
    </Content>
  );
};
